import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@material-ui/core";
import uniqueId from "lodash/uniqueId";
import ChevronRight from "@material-ui/icons/ChevronRight";
import GuestReservationWindow from "./GuestReservationWindow";
import PersonOutline from "@material-ui/icons/PersonOutline";
import Box from "Components/Atoms/Box";
import Button from "Components/Atoms/Button";
import Typography from "Components/Atoms/Typography";
import { AuthContext } from "Contexts/AuthContext";
import useGuestbookVersion from "CustomHooks/useGuestbookVersion";
import { searchGuests, searchGuestsMeilisearch } from "api/GuestBook";
import { GuestSearchResult } from "api/GuestBook/searchGuests";
import { Reservation } from "types/reservations";

type Props = {
    restaurantId: string;
    date: string;
    search: string | null;
    reservations: Reservation[];
    loading: boolean;
};

const ReservationGuestSearch = ({
    restaurantId,
    date,
    search,
    reservations,
    loading
}: Props) => {
    const { t } = useTranslation("reservations");
    const { uid, restaurantId: authRestaurantId } = useContext(AuthContext);
    const { version } = useGuestbookVersion();
    const query = (search || "").trim();
    const dayMatches = reservations.filter(
        (reservation) =>
            !reservation.blockedFullShift &&
            [
                reservation.guest.name,
                reservation.guest.company,
                reservation.customId
            ].some((value) =>
                value?.toLowerCase().includes(query.toLowerCase())
            )
    ).length;
    const enabled =
        !!uid &&
        !!restaurantId &&
        restaurantId === authRestaurantId &&
        !loading &&
        query.length > 3 &&
        dayMatches < 3;
    const scope = JSON.stringify([
        restaurantId,
        date,
        query,
        uid,
        version,
        enabled
    ]);
    const triggerRoot = useRef<HTMLDivElement>(null);
    const [dialogId] = useState(() => uniqueId("reservation-guest-window-"));
    const [selection, setSelection] = useState<{
        scope: string;
        guestId: string;
        anchor: HTMLElement;
    } | null>(null);
    const closeWindow = useCallback(
        (restoreFocus = false) => {
            if (restoreFocus && selection?.anchor.isConnected)
                selection.anchor.focus();
            setSelection(null);
        },
        [selection]
    );
    const containsTrigger = useCallback(
        (target: EventTarget | null) =>
            target instanceof Node && !!triggerRoot.current?.contains(target),
        []
    );
    const [retry, setRetry] = useState(0);
    const [result, setResult] = useState<{
        scope: string;
        guests: GuestSearchResult[];
        loading: boolean;
        failed: boolean;
    } | null>(null);

    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        setResult({ scope, guests: [], loading: true, failed: false });
        const timer = setTimeout(async () => {
            try {
                const params = { restaurantId, search: query, perPage: 5 };
                const guests =
                    version === "v02"
                        ? await searchGuestsMeilisearch(params)
                        : await searchGuests({
                              ...params,
                              uid: uid || undefined,
                              version: "v01"
                          });
                if (!cancelled)
                    setResult({ scope, guests, loading: false, failed: false });
            } catch {
                if (!cancelled)
                    setResult({
                        scope,
                        guests: [],
                        loading: false,
                        failed: true
                    });
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [enabled, scope, restaurantId, query, version, uid, retry]);

    if (!enabled) return null;
    const current = result?.scope === scope ? result : null;

    const selectedGuest =
        selection?.scope === scope
            ? current?.guests.find((guest) => guest.id === selection.guestId)
            : undefined;

    return (
        <div ref={triggerRoot} className="reservation-guest-search">
            <Box padding="md" onClick={(event) => event.stopPropagation()}>
                <Typography variant="text-3" translation="reservations" block>
                    Guests across all dates
                </Typography>
                <div aria-live="polite" aria-busy={!current || current.loading}>
                    {!current || current.loading ? (
                        <Box flex style={{ alignItems: "center", gap: 8 }}>
                            <CircularProgress size={16} />
                            <Typography
                                variant="text-4"
                                translation="reservations"
                            >
                                Searching guest book
                            </Typography>
                        </Box>
                    ) : current.failed ? (
                        <>
                            <Typography
                                variant="text-4"
                                translation="reservations"
                                block
                            >
                                Guest search unavailable
                            </Typography>
                            <Button
                                variant="transparent"
                                onClick={() => setRetry((value) => value + 1)}
                            >
                                {t("Retry guest search")}
                            </Button>
                        </>
                    ) : !current.guests.length ? (
                        <Typography variant="text-4" translation="reservations">
                            No matching guests across dates
                        </Typography>
                    ) : (
                        <>
                            {current.guests.map((guest) => (
                                <Button
                                    key={guest.id}
                                    variant="outlined"
                                    fullWidth
                                    startIcon={() => <PersonOutline />}
                                    endIcon={() => <ChevronRight />}
                                    className={`reservation-guest-search-row${
                                        selectedGuest?.id === guest.id
                                            ? " selected"
                                            : ""
                                    }`}
                                    aria-haspopup="dialog"
                                    aria-expanded={
                                        selectedGuest?.id === guest.id
                                    }
                                    aria-controls={
                                        selectedGuest?.id === guest.id
                                            ? dialogId
                                            : undefined
                                    }
                                    onClick={(event) => {
                                        if (
                                            event.currentTarget instanceof
                                            HTMLElement
                                        )
                                            setSelection({
                                                scope,
                                                guestId: guest.id,
                                                anchor: event.currentTarget
                                            });
                                    }}
                                    aria-label={`${guest.name}: ${t(
                                        "View guest reservations"
                                    )}`}
                                >
                                    <Typography variant="text-3" block>
                                        {guest.name}
                                    </Typography>
                                    <Typography variant="text-4" block>
                                        {guest.email || guest.phone}
                                    </Typography>
                                </Button>
                            ))}
                            {current.guests.length === 5 && (
                                <Typography
                                    variant="text-4"
                                    translation="reservations"
                                    block
                                >
                                    Refine search for more guests
                                </Typography>
                            )}
                        </>
                    )}
                </div>
            </Box>
            {selectedGuest && selection && (
                <GuestReservationWindow
                    key={`${scope}:${selectedGuest.id}`}
                    restaurantId={restaurantId}
                    guest={selectedGuest}
                    anchorEl={selection.anchor}
                    search={query}
                    onClose={closeWindow}
                    containsTrigger={containsTrigger}
                    dialogId={dialogId}
                />
            )}
        </div>
    );
};

export default ReservationGuestSearch;
